/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { toJS } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import { create } from '../../permissions/modelMasters';
import TypeManufacturersProductsStore from 'stores/ManufacturersProductsStore.js';
import ActionDrawerStore from 'stores/ActionDrawerStore.js';

import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import PageTitle from 'components/PageTitleLegacy/PageTitle.js';
import PageWrapper from 'components/PageWrapper/PageWrapper.js';
import FinderWrapper from 'components/FinderWrapper/FinderWrapper.js';
import FinderGrid from 'components/FinderGrid/FinderGrid.js';

import {
  ArchiveManufacturer,
  UnarchiveManufacturer,
  ArchiveProduct,
  UnarchiveProduct,
} from 'views/ActionDrawer';

import ManufacturerList from './components/ManufacturerList/ManufacturerList';
import ManufacturerDetailsActivity from './components/ManufacturerDetailsActivity/ManufacturerDetailsActivity';
import ProductList from './components/ProductList/ProductList';
import ProductDetailsActivity from './components/ProductDetailsActivity/ProductDetailsActivity';
import { Roles } from '../../permissions/defs';

type Props = {|
  manufacturersProductsStore: TypeManufacturersProductsStore,
  actionDrawerStore: ActionDrawerStore,
|} & TranslatorProps;

@translate()
@inject('manufacturersProductsStore', 'actionDrawerStore', 'userStore')
@observer
export default class ManufacturersProducts extends React.Component<Props> {
  componentDidMount() {
    this.props.manufacturersProductsStore.loadManufacturers();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.actionDrawerStore.loadParent) {
      this.props.manufacturersProductsStore.loadManufacturers();
      this.props.actionDrawerStore.loadParent = false;
    }
  }

  componentWillUnmount() {
    this.props.manufacturersProductsStore.resetStore();
    this.props.actionDrawerStore.closeActionDrawer();
  }

  handleActionDrawer = (currentAction: string) => {
    this.props.actionDrawerStore.openActionDrawer(currentAction, {});
  };

  render() {
    const { manufacturersProductsStore, t } = this.props;
    const { actionDrawerStore, userStore } = this.props;
    const isSystemRegionalAdmin = userStore.userRoleId === Roles.SYSTEM_ADMIN_REGIONAL;

    return (
      <React.Fragment>
        <PageWrapper>
          <PageTitle
            title={t('general:model_master_plural')}
            addButton={true}
            permissions={create}
            addMultipleLinks={[
              {
                id: 0,
                label: 'general:manufacturer',
                link: '/manufacturers/create',
              },
              {
                id: 1,
                label: 'general:product',
                link: '/products/create',
              },
            ]}
          />
          <FinderWrapper>
            <FinderGrid>
              <ManufacturerList
                finderColumn={{
                  columnTitle: t('general:manufacturer_plural'),
                  searchList: true,
                  columnData: manufacturersProductsStore.manufacturers,
                  field2: 'aliases',
                }}
                manufacturerStatus={['Pending Review', 'Approved', 'Not Approved', 'Archived']}
              />
              <ManufacturerDetailsActivity
                finderColumn={{
                  columnTitle: t('general:manufacturers.labels.details'),
                  editItem: !isSystemRegionalAdmin,
                  archiveItem: !isSystemRegionalAdmin,
                  archiveAction: () => {
                    this.handleActionDrawer('archiveManufacturer');
                  },
                  unarchiveAction: () => {
                    this.handleActionDrawer('unarchiveManufacturer');
                  },
                  itemType: 'manufacturers',
                  itemData: toJS(manufacturersProductsStore.selectedManufacturer),
                }}
              />
              <ProductList
                finderColumn={{
                  columnTitle: t('general:product_plural'),
                  searchList: true,
                  field: 'model',
                  columnData: manufacturersProductsStore.products,
                }}
              />
              <ProductDetailsActivity
                finderColumn={{
                  columnTitle: t('general:products.labels.details'),
                  editItem: !isSystemRegionalAdmin,
                  archiveItem: !isSystemRegionalAdmin,
                  archiveAction: () => {
                    this.handleActionDrawer('archiveProduct');
                  },
                  unarchiveAction: () => {
                    this.handleActionDrawer('unarchiveProduct');
                  },
                  itemType: 'products',
                  itemData: manufacturersProductsStore.selectedProduct,
                }}
              />
            </FinderGrid>
          </FinderWrapper>
        </PageWrapper>
        <ActionDrawerWrapper
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              archiveManufacturer: (
                <ArchiveManufacturer
                  manufacturer={manufacturersProductsStore.selectedManufacturer}
                />
              ),
              unarchiveManufacturer: (
                <UnarchiveManufacturer
                  manufacturer={manufacturersProductsStore.selectedManufacturer}
                />
              ),
              archiveProduct: (
                <ArchiveProduct product={manufacturersProductsStore.selectedProduct} />
              ),
              unarchiveProduct: (
                <UnarchiveProduct product={manufacturersProductsStore.selectedProduct} />
              ),
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
